import { useContext, useEffect, useRef } from 'react';
import { SessionContext } from '@contexts/SessionProvider';
import { processImage, processData } from '@config/getSessionApis';

/**
 * Hook that watches attachFile and attachDataFile from SessionContext.
 * When a file is attached, it immediately sends it to the backend processing
 * pipeline and stores the processed result in context.
 *
 * - Images: POST /attachment/processImage -> resized PNG blob
 * - Data files: POST /attachment/processData -> TOON string
 *
 * Sets isProcessingAttachment = true while any file is being processed.
 * The Generate button should be disabled while isProcessingAttachment is true.
 */
const useFileProcessor = () => {
    const {
        attachFile,
        attachDataFile,
        setProcessedImage,
        setProcessedDataToon,
        setProcessedDataFileName,
        setIsProcessingAttachment,
        setAttachmentProcessingError,
        setAttachFile,
        setAttachDataFile,
    } = useContext(SessionContext);

    // Track how many processing operations are in-flight
    const processingCountRef = useRef(0);

    // Track which file instances we've already started processing
    // to avoid re-processing on re-renders
    const lastProcessedImageRef = useRef<File | null>(null);
    const lastProcessedDataRef = useRef<File | null>(null);

    // Process image when attachFile changes
    useEffect(() => {
        if (!attachFile) {
            // File was removed — clear processed result
            setProcessedImage(null);
            lastProcessedImageRef.current = null;
            return;
        }

        // Skip if we already started processing this exact file instance
        if (attachFile === lastProcessedImageRef.current) {
            return;
        }
        lastProcessedImageRef.current = attachFile;

        const process = async () => {
            processingCountRef.current += 1;
            setIsProcessingAttachment(true);
            setAttachmentProcessingError(null);

            try {
                const blob = await processImage(attachFile);
                setProcessedImage(blob);
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to process image';
                console.error('[FileProcessor] Image processing failed:', message);
                setAttachmentProcessingError(message);
                setAttachFile(null);
                setProcessedImage(null);
            } finally {
                processingCountRef.current -= 1;
                if (processingCountRef.current <= 0) {
                    processingCountRef.current = 0;
                    setIsProcessingAttachment(false);
                }
            }
        };

        process();
    }, [attachFile]); // eslint-disable-line react-hooks/exhaustive-deps

    // Process data file when attachDataFile changes
    useEffect(() => {
        if (!attachDataFile) {
            // File was removed — clear processed result
            setProcessedDataToon(null);
            setProcessedDataFileName(null);
            lastProcessedDataRef.current = null;
            return;
        }

        // Skip if we already started processing this exact file instance
        if (attachDataFile === lastProcessedDataRef.current) {
            return;
        }
        lastProcessedDataRef.current = attachDataFile;

        const process = async () => {
            processingCountRef.current += 1;
            setIsProcessingAttachment(true);
            setAttachmentProcessingError(null);

            try {
                const toon = await processData(attachDataFile, 25);
                setProcessedDataToon(toon);
                setProcessedDataFileName(attachDataFile.name);
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to process data file';
                console.error('[FileProcessor] Data processing failed:', message);
                setAttachmentProcessingError(message);
                setAttachDataFile(null);
                setProcessedDataToon(null);
                setProcessedDataFileName(null);
            } finally {
                processingCountRef.current -= 1;
                if (processingCountRef.current <= 0) {
                    processingCountRef.current = 0;
                    setIsProcessingAttachment(false);
                }
            }
        };

        process();
    }, [attachDataFile]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useFileProcessor;
